import {
  CaretDownOutlined,
  DeleteOutlined,
  EditOutlined,
  HomeOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Breadcrumb,
  Button,
  Dropdown,
  Input,
  Menu,
  Pagination,
  Popconfirm,
  Select,
  Spin,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import {debounce} from 'lodash';
import {useEffect, useMemo, useState} from 'react';
import toast from 'react-hot-toast';
import {useSelector} from 'react-redux';
import {Link} from 'react-router-dom';

import {RQDeleteuserAPI, RQGetAllUsers, RQUpdateUserProfileAPI} from '../../../reactQuery/apis';
import {selectAuth} from '../../../redux/selectors';

const {Option} = Select;

const UserDetails = () => {
  const [searchInput, setSearchInput] = useState('');

  const [filteredData, setFilteredData] = useState([]);

  const [users, setUsers] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);

  const [pageSize, setPageSize] = useState(10);

  const auth = useSelector(selectAuth);

  const token = auth?.tokenData;

  const {data: userData, isLoading, error, refetch} = RQGetAllUsers(token);
  const pageSizeOptions = ['10', '20', '30', '50'];

  // Update user status Mutation
  const {mutate: updateUserProfile} = RQUpdateUserProfileAPI({
    onSuccess: () => {
      toast.success('User updated successfully');
      refetch();
    },
    onError: error => {
      toast.error(error?.message);
    },
  });

  const debounceSearch = useMemo(() => {
    return debounce(value => {
      const searchableFields = ['name', 'email', 'address', 'mobile'];

      const filtered = users.filter(item =>
        searchableFields.some(field =>
          item[field]?.toString().toLowerCase().includes(value.toLowerCase()),
        ),
      );

      setFilteredData(filtered);
      setCurrentPage(1);
    }, 300);
  }, [users]);

  const handleInputChange = e => {
    const value = e.target.value;
    setSearchInput(value);
    debounceSearch(value);
  };

  // Handle Status Change
  const handleStatusChange = (record, newStatus) => {
    updateUserProfile({
      token,
      id: record.id,
      formData: {
        status: newStatus,
      },
    });
  };

  //DELETE USER 
  const {mutate: deleteuser} = RQDeleteuserAPI({
    onSuccess: () => {
      toast.success('User deleted');
      refetch();
    },
  });
  // Format and set Users from API
  useEffect(() => {
    if (userData && userData.length > 0) {
      const formatted = userData.map((user, index) => ({
        key: user.id,
        srno: index + 1,
        name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
        email: user.email,
        mobile: user.mobile,
        address: user.address,
        pinCode: user.pinCode,
        status: user.status,
        id: user.id,
      }));

      setUsers(formatted);
      setFilteredData(formatted);
    }
  }, [userData]);

  const renderStatusTag = status => {
    const color = status === 'ACTIVE' ? 'green' : 'red';
    return <Tag color={color}>{status}</Tag>;
  };

  const columns = [
    {title: 'Sr. No.', dataIndex: 'srno', key: 'srno', width: 50},
    {title: 'Name', dataIndex: 'name', key: 'name', width: 150},
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 200,
      ellipsis: true,
      render: email => (
        <Tooltip title={email}>
          <span>{email}</span>
        </Tooltip>
      ),
    },
    {title: 'Mobile', dataIndex: 'mobile', key: 'mobile', width: 120},
    {title: 'Address', dataIndex: 'address', key: 'address', width: 180},
    {title: 'Pincode', dataIndex: 'pinCode', key: 'pincode', width: 100},

    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 70,
      render: (status, record) => (
        <Dropdown
          style={{width: '100%'}}
          overlay={(
            <Menu onClick={e => {
              const newStatus = e.key;
              if (newStatus !== record.status) {
                updateUserProfile({
                  token,
                  id: record.id,
                  formData: {
                    status: newStatus,
                  },
                });
              }
            }}>
              {status !== 'ACTIVE' && (
                <Menu.Item key={'ACTIVE'}>
                  {renderStatusTag('ACTIVE')}
                </Menu.Item>
              )}
              {status !== 'INACTIVE' && (
                <Menu.Item key={'INACTIVE'}>
                  {renderStatusTag('INACTIVE')}
                </Menu.Item>
              )}
            </Menu>
          )}
          trigger={['click']}>
          <div className={'cursor-pointer w-fit'}>
            {renderStatusTag(status, true)}              
          </div>  
        </Dropdown>  
      ),
    },

    {
      title: 'Action',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <>
          <Tooltip className={'mr-6'} title={'Edit user'}>
            <Link to={`/admin/user/${record.id}`}>
              <EditOutlined />
            </Link>
          </Tooltip>

          <Tooltip title={'Delete'}>
            <Popconfirm
              title={'Are you sure you want to delete this User?'}
              onConfirm={() => deleteuser({token, id: record.id})}
              okText={'Yes'}
              cancelText={'No'}
            >
              <DeleteOutlined className={'text-red-600'} />
            </Popconfirm>
          
          </Tooltip>
        </>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className={'flex justify-center items-center'}>
        <Spin size={'large'} />
      </div>
    );
  }

  if (error) {
    return <Alert message={'Failed to load users'} type={'error'} />;
  }

  // Pagination logic
  const paginatedData = filteredData.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  return (
    <div className={' lg:p-6'}>

      {/* Header */}
      <div className={'flex md:justify-between justify-end items-center'}>
        <Breadcrumb
          className={'md:flex hidden'}
          items={[
            {href: '/admin/dashboard', title: <HomeOutlined />},
            {
              href: '',
              title: (
                <>
                  <UserOutlined /> <span>User</span>
                </>
              ),
            },
          ]}
        />

        <Input
          allowClear
          placeholder={'Search...'}
          value={searchInput}
          onChange={handleInputChange}
          className={'md:w-2/3 w-full'}
        />
      </div>

      {/* Table */}
      <div className={'mt-4 overflow-x-auto'}>
        <Table
          columns={columns}
          dataSource={paginatedData}
          rowKey={row => row.id}
          pagination={false}
          scroll={{x: 'max-content'}}
        />
      </div>

      {/* Pagination & Page Size */}
      <div className={'flex justify-between items-center mt-4 px-2'}>
        <Select
          value={String(pageSize)}
          onChange={value => {
            setPageSize(Number(value));
            setCurrentPage(1);
          }}
          style={{width: 140}}
        >
          {pageSizeOptions.map(size => (
            <Option key={size} value={size}>
              {size} / page
            </Option>
          ))}
        </Select>

        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={filteredData.length}
          showSizeChanger={false}
          onChange={page => setCurrentPage(page)}
        />
      </div>
    </div>
  );
};

export default UserDetails;
